import client from './client';

export interface AiSettingsResponse {
  provider: string;
  model_id: string;
  free_only: boolean;
  enabled: boolean;
  has_key: boolean;
  source: 'tenant' | 'platform';
  is_configured: boolean;
}

export interface AiSettingsInput {
  provider: string;
  api_key?: string;
  model_id: string;
  free_only: boolean;
  enabled: boolean;
}

export interface AiModel {
  id: string;
  name: string;
}

export interface CampaignDesignInput {
  objective: string;
  audience: string;
  difficulty: string;
  industry: string;
  attack_vector: string;
}

export interface CampaignDesignResponse {
  campaign_name: string;
  subject: string;
  email_html: string;
  email_text: string;
  landing_html: string;
  schedule: string;
  difficulty: string;
  rationale: string;
  raw?: string;
}

export interface GroupSuggestInput {
  scenario: string;
  grouping_by: string;
  max_groups: number;
}

export interface GroupSuggestion {
  name: string;
  rationale: string;
  emails: string[];
  positions: string[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatInput {
  message: string;
  history: ChatMessage[];
}

export interface ChatResponse {
  reply: string;
  sources?: string[];
}

export interface LureInput {
  prompt: string;
  brand?: string;
  scenario?: string;
  tone?: string;
}

export interface LureResponse {
  subject: string;
  html: string;
  text: string;
}

export interface ContentAssistMetadata {
  name?: string;
  subject?: string;
  category?: string;
  difficulty?: string;
  attack_vector?: string;
  impersonated_brand?: string;
}

export interface ContentAssistInput {
  target: 'email_subject' | 'email_html' | 'email_text' | 'landing_html';
  action: 'generate' | 'rewrite' | 'improve' | 'shorten';
  tone?: '' | 'professional' | 'urgent' | 'neutral' | 'friendly';
  instruction?: string;
  selected_text?: string;
  before_text?: string;
  after_text?: string;
  document_html?: string;
  document_text?: string;
  metadata?: ContentAssistMetadata;
}

export interface ContentAssistResponse {
  target: ContentAssistInput['target'];
  replacement: string;
}

// The API reports an unset platform provider as "none". Sending that back is
// rejected; CommandCode is the workspace default.
export function selectedProvider(provider: string | undefined): string {
  const value = (provider || '').trim().toLowerCase();
  if (!value || value === 'none') return 'commandcode';
  return value;
}

export const aiApi = {
  // Settings
  getSettings: () =>
    client.get<AiSettingsResponse>('/ai/settings'),

  saveSettings: (data: AiSettingsInput) =>
    client.put<{ success: boolean; message: string }>('/ai/settings', data),

  deleteSettings: () =>
    client.delete<{ success: boolean; message: string }>('/ai/settings'),

  testConnection: () =>
    client.post<{ success: boolean; message: string }>('/ai/settings/test'),

  getModels: () =>
    client.get<{ provider: string; models: AiModel[] }>('/ai/models'),

  // Campaign Designer
  designCampaign: (data: CampaignDesignInput) =>
    client.post<CampaignDesignResponse>('/ai/campaign-design', data),

  generateLure: (data: LureInput) =>
    client.post<LureResponse>('/ai/lures', data),

  contentAssist: (data: ContentAssistInput) =>
    client.post<ContentAssistResponse>('/ai/content-assist', data),

  // Group Suggestions
  suggestGroups: (data: GroupSuggestInput) =>
    client.post<{ groups: GroupSuggestion[] }>('/ai/suggest-groups', data),

  // Chatbot
  chat: (data: ChatInput) =>
    client.post<ChatResponse>('/ai/chat', data),
};
